/**
 * ErrorBoundary Component
 *
 * Catches React errors and reports them to the backend telemetry service.
 * Displays a fallback UI when an error occurs.
 */

import type { ErrorInfo, ReactNode } from "react";
import React, { Component } from "react";
import { telemetry } from "../../services/telemetryService";

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // Update state with error info
        this.setState({ errorInfo });

        // Report to telemetry service
        void telemetry.reportError(error, errorInfo.componentStack || undefined, {
            componentName: "ErrorBoundary",
            reactVersion: React.version
        });

        // Call optional error handler
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }

        // Also log to console for development
        console.error("[ErrorBoundary] Caught error:", error);
        console.error("[ErrorBoundary] Component stack:", errorInfo.componentStack);
    }

    handleRetry = (): void => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    };

    render(): ReactNode {
        if (this.state.hasError) {
            // Render fallback UI if provided
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default fallback UI
            return (
                <div
                    style={{
                        padding: "20px",
                        margin: "20px",
                        backgroundColor: "#FEE2E2",
                        borderRadius: "8px",
                        border: "1px solid #F87171"
                    }}
                >
                    <h2 style={{ color: "#DC2626", marginTop: 0 }}>Something went wrong</h2>
                    <p style={{ color: "#7F1D1D" }}>An unexpected error occurred. Our team has been notified.</p>
                    {this.state.error && (
                        <details style={{ marginTop: "10px" }}>
                            <summary style={{ cursor: "pointer", color: "#991B1B" }}>Error details</summary>
                            <pre
                                style={{
                                    marginTop: "10px",
                                    padding: "10px",
                                    backgroundColor: "#FEF2F2",
                                    borderRadius: "4px",
                                    fontSize: "12px",
                                    overflow: "auto",
                                    maxHeight: "200px"
                                }}
                            >
                                {this.state.error.message}
                                {"\n\n"}
                                {this.state.error.stack}
                            </pre>
                        </details>
                    )}
                    <button
                        onClick={this.handleRetry}
                        style={{
                            marginTop: "15px",
                            padding: "8px 16px",
                            backgroundColor: "#DC2626",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer"
                        }}
                    >
                        Try Again
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

/**
 * Hook for imperative error reporting
 */
export function useErrorReporter() {
    const reportError = (error: Error, metadata?: Record<string, unknown>) => {
        void telemetry.reportError(error, undefined, metadata);
    };

    return { reportError };
}
